import React from 'react';
import {
  type StatusClusteringScope,
  useStatusClustering,
} from '@/adapter/opm-cockpit-backend/useStatusClustering';
import { Badge } from '@/components/ui/badge';
import {
  groupStatusClusteringByVariant,
  type StatusVariant,
} from '@/lib/utils';
import { Skeleton } from './ui/skeleton';

export const StatusClusteringDisplay: React.FC<{
  scope?: StatusClusteringScope;
  showGesamtBadge?: boolean;
}> = ({ scope = 'main', showGesamtBadge = true }) => {
  const {
    data: statusClustering,
    isLoading,
    error,
  } = useStatusClustering(scope);

  if (error) {
    return (
      <div className="text-red-500">Fehler beim Laden der Statusdaten</div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-4">
        <div className="flex gap-2 items-center">
          <span className="text-sm text-text-secondary">Status:</span>
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-6 w-36" />
        </div>
      </div>
    );
  }

  // Calculate total count from status clustering
  const totalCount = statusClustering
    ? Object.values(statusClustering).reduce((sum, count) => sum + count, 0)
    : 0;

  // Group statuses by their color variant using the utility function
  const groupedStatuses = groupStatusClusteringByVariant(
    statusClustering as unknown as Record<string, number>,
  );

  return (
    <div className="flex items-center gap-4">
      <span className="text-sm text-text-secondary">Status:</span>
      {Object.entries(groupedStatuses).map(([variant, statuses]) => (
        <div key={variant} className="flex items-center gap-2">
          {statuses.map(({ value, text, count }) => (
            <Badge
              key={value}
              variant={variant as StatusVariant}
              className="text-xs"
            >
              {text}: {count}
            </Badge>
          ))}
        </div>
      ))}
      {showGesamtBadge ? (
        <Badge variant="secondary" className="text-sm">
          Gesamt-Einträge: {totalCount}
        </Badge>
      ) : null}
    </div>
  );
};
